import type Dialog from '@naturehouse/design-system/components/molecules/dialog/Dialog';
import modalEvents from '../../../events/modalEvents';
import type Modal from '../../../ui/modal';
import type DatePickerCalendar from '../../calendar/webComponents/DatePickerCalendar';
import {
    DatePickerCalendarEvents,
    DatePickerCalendarSelectState
} from '../../calendar/webComponents/DatePickerCalendar';
import ModalComponent from '../../shared/components/ModalComponent';
import '../../../../css/components/editBookingModal.pcss';

export default class EditBookingComponent extends ModalComponent {
    #datepickerCalendar: DatePickerCalendar | null = null;

    #currentArrivalDate: Date | null = new Date();

    #arrivalDateInputElement: HTMLInputElement | null = null;

    #departureDateInputElement: HTMLInputElement | null = null;

    #departureNotification: HTMLElement | null = null;

    #submitButton: HTMLButtonElement | null = null;

    readonly #selectionHandler = (e: Event): void => {
        const calendar = e.target as DatePickerCalendar;

        const event: CustomEvent = e as CustomEvent;
        const start: Date | null = event.detail.start;
        const end: Date | null = event.detail.end;

        this.fillArrivalDepartureInputs(start, end);
        const dateRange = calendar.selectedDateRange;
        const selection = calendar.selection;

        if (selection.start === null) {
            return;
        }

        const currentArrival = this.#currentArrivalDate ? this.#currentArrivalDate : null;

        if (currentArrival !== dateRange.start) {
            const queryParams = new URLSearchParams();
            queryParams.set('arrivalDate', selection.start);
        }

        this.#currentArrivalDate = dateRange.start;
    };

    protected onTemplateInstantiation(modalTemplate: HTMLTemplateElement): void {
        super.onTemplateInstantiation(modalTemplate);

        if (!this.modalElement) {
            return;
        }

        this.#datepickerCalendar = this.modalElement.querySelector(
            'datepicker-calendar'
        ) as DatePickerCalendar;

        this.#arrivalDateInputElement = this.modalElement.querySelector(
            'calendar-input[data-check-in]'
        );

        this.#departureDateInputElement = this.modalElement.querySelector(
            'calendar-input[data-check-out]'
        );

        this.#submitButton = this.modalElement.querySelector('button[type=submit]');

        this.#departureNotification = this.modalElement.querySelector(
            '[data-role=missing-departure-notification]'
        );

        this.#setEventListeners();
    }

    #setEventListeners(): void {
        this.#datepickerCalendar?.addEventListener(
            DatePickerCalendarEvents.DATE_RANGE_SELECTED,
            this.#selectionHandler
        );

        const arrivalDepartureInputs = this.modalElement?.querySelector(
            '[data-name="arrivalDepartureInputs"]'
        ) as HTMLElement;

        arrivalDepartureInputs?.addEventListener(modalEvents.opened, (event: Event) => {
            const customEvent = event as CustomEvent;
            const detail: Modal = customEvent.detail;
            const datepickerCalendar: DatePickerCalendar | null =
                detail.modal.querySelector('datepicker-calendar');

            if (datepickerCalendar === null) {
                return;
            }

            datepickerCalendar.addEventListener(
                DatePickerCalendarEvents.DATE_RANGE_SELECTED,
                this.#selectionHandler
            );
            this.#datepickerCalendar = datepickerCalendar;
        });

        const nhDialog: Dialog | null = document.getElementById('arrivalDepartureModal') as Dialog;

        arrivalDepartureInputs?.addEventListener('click', () => {
            if (nhDialog === null) {
                return;
            }

            nhDialog.showModal();

            const datepickerCalendar: DatePickerCalendar | null =
                nhDialog.querySelector('datepicker-calendar');

            if (datepickerCalendar === null) {
                return;
            }

            datepickerCalendar.addEventListener(
                DatePickerCalendarEvents.DATE_RANGE_SELECTED,
                this.#selectionHandler
            );
            this.#datepickerCalendar = datepickerCalendar;
        });
    }

    protected fillArrivalDepartureInputs(start: Date | null = null, end: Date | null = null): void {
        if (this.#arrivalDateInputElement) {
            this.#arrivalDateInputElement.value =
                start === null ? '' : start.toISOString().split('T')[0];
        }

        if (this.#departureDateInputElement) {
            this.#departureDateInputElement.value =
                end === null ? '' : end.toISOString().split('T')[0];
        }

        if (start !== null && this.#arrivalDateInputElement) {
            this.#arrivalDateInputElement.dispatchEvent(new Event('change'));
        }

        if (end === null) {
            this.#submitButton?.toggleAttribute('disabled', true);
            this.#departureNotification?.toggleAttribute('hidden', false);
            this.#datepickerCalendar!.selectState = DatePickerCalendarSelectState.END;
            return;
        }

        if (this.#departureDateInputElement) {
            this.#departureDateInputElement.dispatchEvent(new Event('change'));
        }

        this.#submitButton?.toggleAttribute('disabled', false);
        this.#departureNotification?.toggleAttribute('hidden', true);
        this.#datepickerCalendar!.selectState = DatePickerCalendarSelectState.START;
    }

    static getClassName(): string {
        return 'EditBookingComponent';
    }
}
